import {
  Box,
  HStack,
  VStack,
  Text,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Icon,
  Badge,
  Progress,
  Flex,
  useColorModeValue,
  Collapse,
  IconButton
} from '@chakra-ui/react'
import { FC, useMemo, useState } from 'react'
import {
  FiTrendingUp,
  FiTrendingDown,
  FiCheckCircle,
  FiTarget,
  FiCalendar,
  FiShoppingCart,
  FiChevronDown,
  FiChevronUp,
  FiMaximize2,
  FiMinimize2
} from 'react-icons/fi'

import usePurchaseSchedule from '@hook/dashboard/use-purchase-schedule'

import { STATUS_CONFIG, formatPercentage, pluralize } from './utils'

const InsightsSection: FC = () => {
  const { statusCounts, statusPercentages, data } = usePurchaseSchedule()
  const [isExpanded, setIsExpanded] = useState(true)
  const [isCollapsed, setIsCollapsed] = useState(false)

  const borderColor = useColorModeValue('gray.200', 'gray.600')

  const hasData = statusCounts.total > 0

  // Totais distintos (cada registro é uma linha fornecedor × loja × categoria)
  const { totalFornecedores, totalCategorias, totalEmpresas } = useMemo(
    () => ({
      totalFornecedores: new Set(data.map(item => item.fornecedorId)).size,
      totalCategorias: new Set(data.map(item => item.categoriaCompactaId)).size,
      totalEmpresas: new Set(data.map(item => item.empresaId)).size
    }),
    [data]
  )

  // Score de saúde: penaliza atrasos (peso 2) e pendências (peso 1), 0–100
  const healthScore = useMemo(() => {
    if (!hasData) return 0
    const criticityScore =
      statusPercentages.emAtraso * 2 + statusPercentages.pendente
    return Math.min(100, Math.max(0, 100 - criticityScore))
  }, [hasData, statusPercentages])

  const healthColor =
    healthScore > 70 ? 'green' : healthScore > 50 ? 'yellow' : 'red'

  const insights = [
    {
      title: 'Score de Saúde',
      value: hasData ? `${healthScore.toFixed(0)}%` : '-',
      icon: healthScore > 70 ? FiTrendingUp : FiTrendingDown,
      color: hasData ? healthColor : 'gray',
      description: !hasData
        ? 'Sem registros no período'
        : healthScore > 70
        ? 'Cronograma saudável'
        : 'Atenção necessária'
    },
    {
      title: 'Fornecedores Ativos',
      value: totalFornecedores.toString(),
      icon: FiShoppingCart,
      color: 'blue',
      description: 'Fornecedores únicos'
    },
    {
      title: 'Categorias',
      value: totalCategorias.toString(),
      icon: FiTarget,
      color: 'purple',
      description: 'Categorias ativas'
    },
    {
      title: 'Empresas',
      value: totalEmpresas.toString(),
      icon: FiCalendar,
      color: 'cyan',
      description: 'Empresas monitoradas'
    }
  ]

  const statusInsights = [
    {
      label: 'Crítico',
      value: statusCounts.emAtraso,
      percentage: statusPercentages.emAtraso,
      icon: STATUS_CONFIG.emAtraso.icon,
      color: 'red',
      description: 'Registros em atraso requerem ação imediata'
    },
    {
      label: 'Atenção',
      value: statusCounts.pendente,
      percentage: statusPercentages.pendente,
      icon: STATUS_CONFIG.pendente.icon,
      color: 'yellow',
      description: 'Registros pendentes precisam de acompanhamento'
    },
    {
      label: 'Saudável',
      value: statusCounts.noPrazo,
      percentage: statusPercentages.noPrazo,
      icon: STATUS_CONFIG.noPrazo.icon,
      color: 'green',
      description: 'Registros dentro do cronograma'
    }
  ]

  return (
    <Box
      bg={'white'}
      p={6}
      borderRadius="lg"
      border="1px solid"
      borderColor={borderColor}
      shadow="md"
    >
      <VStack spacing={6} align="stretch">
        {/* Header */}
        <HStack justify="space-between" align="center">
          <HStack spacing={3}>
            <Icon as={FiTrendingUp} color="blue.500" boxSize="20px" />
            <Text fontSize="xl" fontWeight="bold" color="gray.700">
              Insights do Cronograma
            </Text>
          </HStack>
          <HStack spacing={2}>
            {hasData && (
              <Badge
                colorScheme={healthColor}
                variant="solid"
                px={3}
                py={1}
                borderRadius="full"
              >
                Score: {healthScore.toFixed(0)}%
              </Badge>
            )}
            <IconButton
              aria-label={
                isExpanded ? 'Diminuir insights' : 'Expandir insights'
              }
              icon={<Icon as={isExpanded ? FiMinimize2 : FiMaximize2} />}
              size="sm"
              variant="ghost"
              colorScheme="blue"
              onClick={() => setIsExpanded(!isExpanded)}
            />
            <IconButton
              aria-label={isCollapsed ? 'Mostrar insights' : 'Ocultar insights'}
              icon={<Icon as={isCollapsed ? FiChevronDown : FiChevronUp} />}
              size="sm"
              variant="ghost"
              colorScheme="blue"
              onClick={() => setIsCollapsed(!isCollapsed)}
            />
          </HStack>
        </HStack>

        <Collapse in={!isCollapsed} animateOpacity>
          {!hasData ? (
            <Text fontSize="sm" color="gray.500" textAlign="center" py={4}>
              Nenhum registro encontrado para os filtros selecionados.
            </Text>
          ) : (
            <VStack spacing={6} align="stretch">
              {/* Métricas Principais */}
              <SimpleGrid
                columns={{ base: 2, md: isExpanded ? 4 : 2 }}
                spacing={4}
              >
                {insights.map(insight => (
                  <Stat
                    key={insight.title}
                    bg={`${insight.color}.50`}
                    p={isExpanded ? 4 : 3}
                    borderRadius="md"
                    border="1px solid"
                    borderColor={`${insight.color}.200`}
                  >
                    <Flex justify="space-between" align="center" mb={2}>
                      <Icon
                        as={insight.icon}
                        color={`${insight.color}.500`}
                        boxSize={isExpanded ? '18px' : '16px'}
                      />
                    </Flex>
                    <StatLabel
                      fontSize={isExpanded ? 'sm' : 'xs'}
                      color="gray.600"
                    >
                      {insight.title}
                    </StatLabel>
                    <StatNumber
                      fontSize={isExpanded ? '2xl' : 'xl'}
                      color={`${insight.color}.600`}
                    >
                      {insight.value}
                    </StatNumber>
                    {isExpanded && (
                      <StatHelpText fontSize="xs" color="gray.500" mt={1}>
                        {insight.description}
                      </StatHelpText>
                    )}
                  </Stat>
                ))}
              </SimpleGrid>

              {/* Status Breakdown */}
              {isExpanded && (
                <Box>
                  <Text
                    fontSize="md"
                    fontWeight="semibold"
                    color="gray.700"
                    mb={3}
                  >
                    Distribuição de Status
                  </Text>
                  <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                    {statusInsights.map(status => (
                      <Box
                        key={status.label}
                        bg={`${status.color}.50`}
                        p={4}
                        borderRadius="md"
                        border="1px solid"
                        borderColor={`${status.color}.200`}
                      >
                        <HStack justify="space-between" mb={2}>
                          <HStack spacing={2}>
                            <Icon
                              as={status.icon}
                              color={`${status.color}.500`}
                              boxSize="16px"
                            />
                            <Text
                              fontSize="sm"
                              fontWeight="medium"
                              color="gray.700"
                            >
                              {status.label}
                            </Text>
                          </HStack>
                          <Badge
                            colorScheme={status.color}
                            variant="solid"
                            fontSize="xs"
                          >
                            {status.value}
                          </Badge>
                        </HStack>

                        <Progress
                          value={status.percentage}
                          colorScheme={status.color}
                          size="sm"
                          borderRadius="full"
                          mb={2}
                        />

                        <HStack justify="space-between">
                          <Text
                            fontSize="lg"
                            fontWeight="bold"
                            color={`${status.color}.600`}
                          >
                            {formatPercentage(status.percentage)}
                          </Text>
                          <Text fontSize="xs" color="gray.500">
                            do total
                          </Text>
                        </HStack>

                        <Text fontSize="xs" color="gray.500" mt={1}>
                          {status.description}
                        </Text>
                      </Box>
                    ))}
                  </SimpleGrid>
                </Box>
              )}

              {/* Recomendações */}
              {isExpanded && (
                <Box>
                  <Text
                    fontSize="md"
                    fontWeight="semibold"
                    color="gray.700"
                    mb={3}
                  >
                    Recomendações
                  </Text>
                  <VStack spacing={2} align="stretch">
                    {statusCounts.emAtraso > 0 && (
                      <Box
                        bg="red.50"
                        p={3}
                        borderRadius="md"
                        border="1px solid"
                        borderColor="red.200"
                      >
                        <HStack spacing={2}>
                          <Icon
                            as={STATUS_CONFIG.emAtraso.icon}
                            color="red.500"
                            boxSize="14px"
                          />
                          <Text fontSize="sm" color="red.700">
                            <strong>
                              {pluralize(
                                statusCounts.emAtraso,
                                'registro em atraso',
                                'registros em atraso'
                              )}
                            </strong>{' '}
                            - Priorize o contato com fornecedores
                          </Text>
                        </HStack>
                      </Box>
                    )}

                    {statusCounts.pendente > 0 && (
                      <Box
                        bg="yellow.50"
                        p={3}
                        borderRadius="md"
                        border="1px solid"
                        borderColor="yellow.200"
                      >
                        <HStack spacing={2}>
                          <Icon
                            as={STATUS_CONFIG.pendente.icon}
                            color="yellow.500"
                            boxSize="14px"
                          />
                          <Text fontSize="sm" color="yellow.700">
                            <strong>
                              {pluralize(
                                statusCounts.pendente,
                                'registro pendente',
                                'registros pendentes'
                              )}
                            </strong>{' '}
                            - Agende follow-ups preventivos
                          </Text>
                        </HStack>
                      </Box>
                    )}

                    {healthScore > 80 && (
                      <Box
                        bg="green.50"
                        p={3}
                        borderRadius="md"
                        border="1px solid"
                        borderColor="green.200"
                      >
                        <HStack spacing={2}>
                          <Icon
                            as={FiCheckCircle}
                            color="green.500"
                            boxSize="14px"
                          />
                          <Text fontSize="sm" color="green.700">
                            <strong>Cronograma saudável!</strong> - Continue
                            monitorando para manter a qualidade
                          </Text>
                        </HStack>
                      </Box>
                    )}
                  </VStack>
                </Box>
              )}
            </VStack>
          )}
        </Collapse>
      </VStack>
    </Box>
  )
}

export default InsightsSection
